import React, { useState, useEffect, useRef } from 'react';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ArrowLeft, AlertCircle, Play, CheckCircle, XCircle, 
  Loader2, Activity, Brain, TestTube, Eye, Code
} from 'lucide-react';
import { PlatformConnectionStatus } from '@/components/PlatformConnectionStatus';
import { usePlatformConnectionMode } from '@/lib/usePlatformConnectionMode';

const API = INCIDENTS_API;

export default function ErrorTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ticketPlatformConnected = usePlatformConnectionMode('ticket');
  const streamEndRef = useRef(null);
  
  const [ticket, setTicket] = useState(null);
  const [errorAnalysis, setErrorAnalysis] = useState(null);
  const [resolutionPlan, setResolutionPlan] = useState(null);
  const [executionResults, setExecutionResults] = useState(null);
  const [verification, setVerification] = useState(null);
  const [activityStream, setActivityStream] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [currentPhase, setCurrentPhase] = useState('initial'); // initial, analyzed, executing, verified

  useEffect(() => {
    fetchTicketData();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [activityStream]);

  const scrollToBottom = () => {
    streamEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTicketData = async () => {
    try {
      // id from useParams is ticket_number
      const ticketNumber = id;
      const response = await axios.get(`${API}/tickets/${ticketNumber}`);
      setTicket(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      setLoading(false);
    }
  };

  const analyzeError = async () => {
    setAnalyzing(true);
    setCurrentPhase('analyzing');
    
    // Add to activity stream
    addActivity({
      icon: '🔍',
      title: 'Error Analysis Agent Started',
      message: 'Analyzing error details and searching knowledge base...',
      color: 'bg-cp-indigo'
    });

    try {
      // Simulate agent thinking
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      addActivity({
        icon: '🧠',
        title: 'Checking Knowledge Base',
        message: `Searching for error pattern: "${ticket.error_code}"`,
        color: 'bg-cp-inset'
      });

      const response = await axios.post(`${API}/errors/resolve`, {
        ticket_id: id,
        error_code: ticket.error_code || 'UNKNOWN',
        error_message: ticket.error_message || ticket.description,
        environment: 'dev',
        context: {
          ticket_number: ticket.ticket_number,
          sap_system: ticket.sap_system,
          error_module: ticket.error_module
        }
      });

      setResolutionPlan(response.data);

      // Add result to stream
      if (response.data.source === 'learned_knowledge') {
        addActivity({
          icon: '🎓',
          title: 'Found in Knowledge Base',
          message: `Learned resolution found! Success rate: ${response.data.success_rate?.toFixed(0)}% (${response.data.times_used} prior uses)`,
          color: 'bg-cp-ok'
        });
      } else {
        addActivity({
          icon: '🤖',
          title: 'AI Generated New Resolution',
          message: `Generated custom resolution plan using GPT-4o`,
          color: 'bg-cp-cyan'
        });
      }

      // Show root cause if available
      if (response.data.detailed_resolution?.root_cause_analysis) {
        addActivity({
          icon: '🔎',
          title: 'Root Cause Identified',
          message: response.data.detailed_resolution.root_cause_analysis,
          color: 'bg-cp-faint'
        });
      }

      addActivity({
        icon: '✅',
        title: 'Analysis Complete',
        message: `${response.data.steps.length} resolution steps identified (${(response.data.confidence * 100).toFixed(0)}% confidence)`,
        color: 'bg-cp-ok'
      });

      setCurrentPhase('analyzed');
    } catch (error) {
      console.error('Error analyzing:', error);
      addActivity({
        icon: '❌',
        title: 'Analysis Failed',
        message: error.message,
        color: 'bg-cp-crit'
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const executeResolution = async () => {
    setExecuting(true);
    setCurrentPhase('executing');

    addActivity({
      icon: '🚀',
      title: 'Resolution Execution Started',
      message: `Executing ${resolutionPlan.steps.length} resolution steps...`,
      color: 'bg-cp-inset'
    });

    try {
      const ticketNumber = ticket?.ticket_number || id;
      const response = await axios.post(`${API}/tickets/${ticketNumber}/resolve-error`);
      
      // Show each executed step
      for (const result of response.data.execution_results || []) {
        await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate execution time
        
        addActivity({
          icon: result.status === 'success' ? '✓' : '⚠️',
          title: `Step ${result.step_number}: ${result.transaction_code || 'Executed'}`,
          message: result.result || result.action,
          color: result.status === 'success' ? 'bg-cp-ok' : 'bg-cp-faint',
          details: result
        });
      }

      setExecutionResults(response.data.execution_results);

      // Verification phase
      addActivity({
        icon: '🧪',
        title: 'Verification Started',
        message: 'Testing if error is resolved...',
        color: 'bg-cp-indigo'
      });

      await new Promise(resolve => setTimeout(resolve, 2000));

      const verificationResult = response.data.verification;
      setVerification(verificationResult);

      if (verificationResult?.fixed) {
        addActivity({
          icon: '🎉',
          title: 'Error Successfully Resolved',
          message: `Verified fix with ${(verificationResult.confidence * 100).toFixed(0)}% confidence. ${verificationResult.reasoning}`,
          color: 'bg-cp-ok'
        });
      } else {
        addActivity({
          icon: '⚠️',
          title: 'Error Not Fully Resolved',
          message: verificationResult?.reasoning || 'Additional steps may be needed',
          color: 'bg-cp-faint'
        });
      }

      setCurrentPhase('verified');
    } catch (error) {
      console.error('Error executing:', error);
      addActivity({
        icon: '❌',
        title: 'Execution Failed',
        message: error.response?.data?.detail || error.message,
        color: 'bg-cp-crit'
      });
    } finally {
      setExecuting(false);
    }
  };

  const addActivity = (activity) => {
    setActivityStream(prev => [...prev, {
      id: `activity-${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
      ...activity
    }]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!ticket) {
    return <div className="flex items-center justify-center h-full">Ticket not found</div>;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-cp-surface">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/tickets')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
              <Badge variant="destructive" className="text-xs">ERROR TICKET</Badge>
              {ticket.target_system_name && (
                <Badge className="text-xs bg-cp-indigo">🎯 {ticket.target_system_name}</Badge>
              )}
            </div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-cp-crit" />
              {ticket.title}
            </h1>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <PlatformConnectionStatus connected={ticketPlatformConnected} scope="ticket" />
          {currentPhase === 'initial' && (
            <Button onClick={analyzeError} disabled={analyzing} className="bg-cp-indigo hover:bg-cp-violet">
              {analyzing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="mr-2 h-4 w-4" />
                  Analyze Error
                </>
              )}
            </Button>
          )}
          {currentPhase === 'analyzed' && (
            <Button onClick={executeResolution} disabled={executing} className="bg-cp-crit hover:bg-cp-crit">
              {executing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Execute Resolution
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Error Information Panel */}
      <div className="p-4 bg-cp-inset border-b border-cp-line">
        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-0">
            <div className="text-muted-foreground text-xs">Error Code</div>
            <code className="block break-all font-bold text-cp-crit">{ticket.error_code || 'N/A'}</code>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">SAP Module</div>
            <Badge variant="outline">{ticket.error_module || ticket.sap_system}</Badge>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Priority</div>
            <Badge variant={ticket.priority === 'critical' ? 'destructive' : 'outline'}>
              {ticket.priority}
            </Badge>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Status</div>
            <Badge>{ticket.status.replace('_', ' ')}</Badge>
          </div>
        </div>
        <div className="mt-3 p-3 bg-cp-surface border rounded">
          <div className="text-xs text-muted-foreground mb-1">Error Message:</div>
          <div className="text-sm font-medium">{ticket.error_message || ticket.description}</div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: Live Agent Activity Stream */}
        <div className="flex-1 flex flex-col border-r">
          <div className="p-3 border-b bg-gradient-to-r from-cp-inset to-cp-surface">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-cp-crit animate-pulse" />
              <h2 className="font-semibold">Live Error Resolution Activity</h2>
            </div>
          </div>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              {activityStream.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    Click "Analyze Error" to begin error resolution workflow
                  </AlertDescription>
                </Alert>
              ) : (
                activityStream.map((activity) => (
                  <div key={activity.id} className="flex gap-3 animate-in fade-in slide-in-from-left-2">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full ${activity.color} flex items-center justify-center text-white text-sm shadow-md`}>
                      {activity.icon}
                    </div>
                    <div className="flex-1 bg-cp-surface border rounded-lg p-3 shadow-sm">
                      <div className="flex items-start justify-between mb-1">
                        <div className="font-medium text-sm">{activity.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(activity.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">{activity.message}</div>
                      {activity.details && (
                        <details className="mt-2">
                          <summary className="text-xs text-cp-indigo cursor-pointer">Show Details</summary>
                          <pre className="mt-2 text-xs bg-cp-inset p-2 rounded overflow-x-auto">
                            {JSON.stringify(activity.details, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                ))
              )}
              <div ref={streamEndRef} />
            </div>
          </ScrollArea>
        </div>

        {/* RIGHT: Resolution Plan & Details */}
        <div className="w-2/5 flex flex-col">
          <div className="p-3 border-b bg-gradient-to-r from-cp-inset to-cp-surface">
            <h2 className="font-semibold">Resolution Details</h2>
          </div>

          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {/* Resolution Plan */}
              {resolutionPlan && (
                <Card className="border-2 border-cp-indigo">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Resolution Plan</CardTitle>
                      <div className="flex gap-2">
                        <Badge variant={resolutionPlan.source === 'learned_knowledge' ? 'default' : 'outline'}>
                          {resolutionPlan.source === 'learned_knowledge' ? '🎓 Learned' : '🤖 AI Generated'}
                        </Badge>
                        <Badge className="bg-cp-indigo">
                          {(resolutionPlan.confidence * 100).toFixed(0)}% Confidence
                        </Badge>
                      </div>
                    </div>
                    {resolutionPlan.source === 'learned_knowledge' && (
                      <CardDescription className="text-cp-ok">
                        ✓ Used {resolutionPlan.times_used} times with {resolutionPlan.success_rate?.toFixed(0)}% success
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Root Cause */}
                      {resolutionPlan.detailed_resolution?.root_cause_analysis && (
                        <div className="p-3 bg-cp-inset border border-cp-mod rounded-lg">
                          <div className="text-xs font-semibold text-cp-mod mb-1">🔍 Root Cause Analysis</div>
                          <div className="text-sm">{resolutionPlan.detailed_resolution.root_cause_analysis}</div>
                        </div>
                      )}

                      {/* Resolution Steps */}
                      <div>
                        <div className="text-sm font-semibold mb-2">Resolution Steps:</div>
                        <div className="space-y-2">
                          {resolutionPlan.steps.map((step, idx) => (
                            <div key={idx} className="flex gap-2 p-2 bg-cp-inset rounded border">
                              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-cp-indigo text-white flex items-center justify-center text-xs font-bold">
                                {idx + 1}
                              </div>
                              <div className="flex-1 text-sm">{step}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Prevention Tips */}
                      {resolutionPlan.detailed_resolution?.prevention_tips && (
                        <details className="p-3 bg-cp-inset border border-cp-line rounded-lg">
                          <summary className="text-xs font-semibold text-cp-ok cursor-pointer">
                            💡 Prevention Tips
                          </summary>
                          <ul className="mt-2 text-sm list-disc list-inside space-y-1">
                            {resolutionPlan.detailed_resolution.prevention_tips.map((tip, idx) => (
                              <li key={idx}>{tip}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Execution Results */}
              {executionResults && (
                <Card className="border-2 border-cp-violet">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Code className="h-4 w-4" />
                      Execution Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {executionResults.map((result, idx) => (
                        <div key={idx} className={`p-2 rounded border ${
                          result.status === 'success' ? 'bg-cp-inset border-cp-line' :
                          result.status === 'failed' ? 'bg-cp-inset border-cp-line' :
                          'bg-cp-inset'
                        }`}>
                          <div className="flex items-center gap-2 mb-1">
                            {result.status === 'success' ? (
                              <CheckCircle className="h-4 w-4 text-cp-ok" />
                            ) : (
                              <XCircle className="h-4 w-4 text-cp-crit" />
                            )}
                            <span className="text-sm font-medium">Step {result.step_number}</span>
                            {result.transaction_code && (
                              <code className="text-xs bg-cp-surface px-1 rounded">{result.transaction_code}</code>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">{result.result}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Verification Results */}
              {verification && (
                <Card className={`border-2 ${verification.fixed ? 'border-cp-ok' : 'border-cp-mod'}`}>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TestTube className="h-4 w-4" />
                      Verification Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className={`p-3 rounded-lg ${verification.fixed ? 'bg-cp-inset' : 'bg-cp-inset'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          {verification.fixed ? (
                            <CheckCircle className="h-5 w-5 text-cp-ok" />
                          ) : (
                            <AlertCircle className="h-5 w-5 text-cp-mod" />
                          )}
                          <span className="font-semibold">
                            {verification.fixed ? 'Error Resolved' : 'Error Partially Resolved'}
                          </span>
                        </div>
                        <div className="text-sm">{verification.reasoning}</div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="text-center p-2 bg-cp-surface rounded border">
                          <div className="font-bold">{verification.successful_steps}/{verification.total_steps}</div>
                          <div className="text-xs text-muted-foreground">Steps Successful</div>
                        </div>
                        <div className="text-center p-2 bg-cp-surface rounded border">
                          <div className="font-bold">{(verification.confidence * 100).toFixed(0)}%</div>
                          <div className="text-xs text-muted-foreground">Confidence</div>
                        </div>
                      </div>

                      {verification.additional_checks && (
                        <div className="text-xs">
                          <div className="font-medium mb-1">Additional Checks:</div>
                          <ul className="list-disc list-inside space-y-1">
                            {verification.additional_checks.map((check, idx) => (
                              <li key={idx}>{check}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ticket Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Ticket Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Requester: </span>
                    <span className="font-medium">{ticket.requester_name}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created: </span>
                    <span>{new Date(ticket.created_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Source: </span>
                    <Badge variant="outline">{ticket.source}</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
